/**
 * Booking Options Service
 * -----------------------
 * Stores assessors, schemes and appointment types centrally so every user sees
 * the same list. Previously these lived in browser localStorage, which meant a
 * name added by one person was invisible to everyone else.
 *
 * STORAGE NOTE
 * Same approach as smsMessageConfigService: a reserved record via the existing
 * /comms/sms/templates endpoint, using answers[] as a key/value store. Kept as
 * a separate record from the message wording so that saving one cannot
 * overwrite the other.
 *
 * If a real settings endpoint is added later, only loadOptions() and
 * saveOptions() need to change.
 */

import smsApiService from './smsApiService';

export const OPTIONS_RECORD_NAME = 'SYSTEM CONFIG - Booking Options (do not delete)';

const OPTIONS_RECORD_DESCRIPTION =
  'Reserved record holding shared assessors, schemes and appointment types for '
  + 'the Booking screen. Managed via Manage Booking Options. Not a sendable flow.';

const LEGACY_LOCAL_KEY = 'karmaBookingOptions';

/* ------------------------------------------------------------------ *
 * Encoding                                                            *
 * ------------------------------------------------------------------ */

const kv = (obj) => Object.entries(obj || {}).map(([key, value]) => ({
  text: key,
  response: String(value ?? ''),
  nextStageNumber: null
}));

const answersToObject = (answers) => {
  const output = {};
  (Array.isArray(answers) ? answers : []).forEach((answer) => {
    // The DB round-trip returns Sequelize column names (AnswerText/ResponseMessage),
    // not the text/response shape used when saving; without this, every decode was empty.
    const key = answer?.text ?? answer?.Text ?? answer?.AnswerText;
    if (key === undefined || key === null || key === '') return;
    output[key] = answer?.response ?? answer?.Response ?? answer?.ResponseMessage ?? '';
  });
  return output;
};

const encodeToRecord = (options, meta) => ({
  name: OPTIONS_RECORD_NAME,
  description: OPTIONS_RECORD_DESCRIPTION,
  stages: [
    {
      question: 'ASSESSORS',
      isInitial: true,
      // Index keys are zero-padded so the list order survives a round trip.
      answers: (options.assessorOptions || []).map((name, index) => ({
        text: String(index).padStart(3, '0'),
        response: String(name ?? ''),
        nextStageNumber: null
      })),
      defaultResponse: null
    },
    ...(options.schemeOptions || []).map((scheme) => ({
      question: `SCHEME:${scheme.id}`,
      isInitial: false,
      answers: kv({
        id: scheme.id,
        label: scheme.label,
        schemeName: scheme.schemeName,
        company: scheme.company,
        schemeNumber: scheme.schemeNumber
      }),
      defaultResponse: null
    })),
    ...(options.appointmentTypes || []).map((type) => ({
      question: `TYPE:${type.id}`,
      isInitial: false,
      answers: kv({
        id: type.id,
        label: type.label,
        body: type.body,
        duration: type.duration
      }),
      defaultResponse: null
    })),
    {
      question: 'META',
      isInitial: false,
      answers: kv({
        updatedAt: meta.updatedAt || '',
        updatedBy: meta.updatedBy || ''
      }),
      defaultResponse: null
    }
  ]
});

const decodeFromRecord = (record) => {
  const stages = record?.stages || record?.Stages || [];
  const decoded = {
    assessorOptions: [],
    schemeOptions: [],
    appointmentTypes: [],
    updatedAt: null,
    updatedBy: null
  };

  stages.forEach((stage) => {
    const marker = String(stage?.question ?? stage?.Question ?? '').trim();
    const values = answersToObject(stage?.answers ?? stage?.Answers);

    if (marker === 'ASSESSORS') {
      decoded.assessorOptions = Object.keys(values)
        .sort()
        .map((key) => values[key])
        .filter((name) => String(name).trim());
    } else if (marker === 'META') {
      decoded.updatedAt = values.updatedAt || null;
      decoded.updatedBy = values.updatedBy || null;
    } else if (marker.startsWith('SCHEME:')) {
      decoded.schemeOptions.push({
        id: values.id || marker.slice(7),
        label: values.label || '',
        schemeName: values.schemeName || '',
        company: values.company || '',
        schemeNumber: values.schemeNumber || ''
      });
    } else if (marker.startsWith('TYPE:')) {
      decoded.appointmentTypes.push({
        id: values.id || marker.slice(5),
        label: values.label || '',
        body: values.body || '',
        duration: values.duration || ''
      });
    }
  });

  return decoded;
};

/** A decoded record is only usable if it actually contains options. */
const isUsable = (decoded) => Boolean(
  decoded
  && (decoded.assessorOptions?.length
    || decoded.schemeOptions?.length
    || decoded.appointmentTypes?.length)
);

const findRecord = async (instance) => {
  const response = await smsApiService.getTemplates(instance);
  const templates = Array.isArray(response?.data) ? response.data : [];
  return templates.find(
    (template) => String(template?.Name || template?.name || '').trim() === OPTIONS_RECORD_NAME
  ) || null;
};

const recordId = (record) => {
  const parsed = Number.parseInt(record?.Id ?? record?.id, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Anything previously saved in this browser, so local edits are not lost. */
export const readLegacyLocalOptions = () => {
  try {
    const stored = localStorage.getItem(LEGACY_LOCAL_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ *
 * Public API                                                          *
 * ------------------------------------------------------------------ */

/**
 * Load the shared options.
 *
 * Order of preference:
 *   1. the shared record, if it exists
 *   2. whatever this browser had in localStorage (so an admin's existing
 *      additions become the starting point rather than being wiped)
 *   3. the built-in defaults passed in by the caller
 *
 * Never throws.
 *
 * @param instance MSAL instance
 * @param fallback built-in defaults, used if nothing else is available
 * @returns {Promise<{options: object, source: 'shared'|'local'|'defaults', updatedAt: ?string, updatedBy: ?string, error: ?string}>}
 */
export const loadOptions = async (instance, fallback) => {
  const localOptions = readLegacyLocalOptions();

  try {
    const record = await findRecord(instance);

    if (record) {
      const id = recordId(record);
      const full = id ? (await smsApiService.getTemplate(id, instance))?.data : record;
      const decoded = decodeFromRecord(full);

      if (isUsable(decoded)) {
        return {
          options: {
            assessorOptions: decoded.assessorOptions,
            schemeOptions: decoded.schemeOptions,
            appointmentTypes: decoded.appointmentTypes
          },
          source: 'shared',
          updatedAt: decoded.updatedAt,
          updatedBy: decoded.updatedBy,
          error: null
        };
      }
    }

    const localUsable = isUsable(localOptions);
    return {
      options: localUsable ? localOptions : fallback,
      source: localUsable ? 'local' : 'defaults',
      updatedAt: null,
      updatedBy: null,
      error: null
    };
  } catch (error) {
    console.error('[bookingOptions] Falling back to local options:', error);
    const localUsable = isUsable(localOptions);
    return {
      options: localUsable ? localOptions : fallback,
      source: localUsable ? 'local' : 'defaults',
      updatedAt: null,
      updatedBy: null,
      error: error?.response?.data?.error || error?.message || 'Could not load shared options.'
    };
  }
};

/**
 * Publish the options so every user sees them.
 * @returns {Promise<{ok: boolean, error: ?string, updatedAt: ?string}>}
 */
export const saveOptions = async (options, instance, updatedBy) => {
  try {
    const updatedAt = new Date().toISOString();
    const payload = encodeToRecord(options, { updatedAt, updatedBy });

    const existing = await findRecord(instance);
    const id = recordId(existing);

    if (id) {
      await smsApiService.updateTemplate(id, payload, instance);
    } else {
      await smsApiService.saveTemplate(payload, instance);
    }

    return { ok: true, error: null, updatedAt };
  } catch (error) {
    console.error('[bookingOptions] Save failed:', error);
    return {
      ok: false,
      updatedAt: null,
      error: error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || 'Could not save. Your changes are still only on this computer.'
    };
  }
};

const bookingOptionsService = {
  loadOptions,
  saveOptions,
  readLegacyLocalOptions,
  OPTIONS_RECORD_NAME
};

export default bookingOptionsService;
